import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

function ChannelBadge({ channel }) {
  const map = {
    email: 'bg-blue-50 text-blue-700',
    sms: 'bg-purple-50 text-purple-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[channel] || 'bg-secondary-100 text-secondary-600'}`}>
      {channel}
    </span>
  );
}

const TEMPLATE_VARIABLES = [
  '{{patient_name}}',
  '{{device_id}}',
  '{{caregiver_name}}',
  '{{facility_name}}',
  '{{alert_type}}',
  '{{alert_message}}',
  '{{timestamp}}',
];

export default function Templates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [channelFilter, setChannelFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ subject: '', body: '', active: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/notification-templates');
      setTemplates(data.data || data);
    } catch {} finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const startEdit = (template) => {
    setEditingId(template.id);
    setEditForm({
      subject: template.subject || '',
      body: template.body || '',
      active: template.active !== false,
    });
    setError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ subject: '', body: '', active: true });
    setError('');
  };

  const handleSave = async (id) => {
    setSaving(true);
    setError('');
    try {
      await api.put(`/notification-templates/${id}`, editForm);
      setEditingId(null);
      fetchTemplates();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (template) => {
    try {
      await api.put(`/notification-templates/${template.id}`, {
        subject: template.subject,
        body: template.body,
        active: !template.active,
      });
      fetchTemplates();
    } catch {}
  };

  const filtered = channelFilter
    ? templates.filter((t) => t.channel === channelFilter)
    : templates;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Notification Templates</h1>
          <p className="text-secondary-500 text-sm mt-1">Manage email and SMS notification templates</p>
        </div>
      </div>

      {/* Variable hint */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-3">
        <p className="text-sm font-medium text-blue-800 mb-1">Available template variables</p>
        <div className="flex flex-wrap gap-2">
          {TEMPLATE_VARIABLES.map((v) => (
            <code key={v} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-mono">{v}</code>
          ))}
        </div>
      </div>

      {/* Channel filter */}
      <div className="flex gap-2">
        <span className="text-sm text-secondary-500 self-center mr-1">Channel:</span>
        {['', 'email', 'sms'].map((c) => (
          <button key={c} onClick={() => setChannelFilter(c)}
            className={`px-3 py-1.5 text-sm rounded-lg border transition ${
              channelFilter === c
                ? 'bg-primary-50 border-primary-200 text-primary-700'
                : 'border-secondary-200 text-secondary-600 hover:bg-secondary-50'
            }`}>
            {c || 'All'}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary-50 text-secondary-600">
              <tr>
                <th className="px-5 py-3 text-left font-medium">Channel</th>
                <th className="px-5 py-3 text-left font-medium">Template Key</th>
                <th className="px-5 py-3 text-left font-medium">Subject</th>
                <th className="px-5 py-3 text-left font-medium">Active</th>
                <th className="px-5 py-3 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {loading && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-secondary-400">Loading...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={5} className="px-5 py-8 text-center text-secondary-400">No templates found</td></tr>
              )}
              {!loading && filtered.map((t) => (
                <React.Fragment key={t.id}>
                  <tr className="hover:bg-secondary-50 transition">
                    <td className="px-5 py-3"><ChannelBadge channel={t.channel} /></td>
                    <td className="px-5 py-3 text-secondary-700 font-mono text-xs">{t.template_key}</td>
                    <td className="px-5 py-3 text-secondary-700 max-w-xs truncate">{t.subject || '--'}</td>
                    <td className="px-5 py-3">
                      <button
                        onClick={() => handleToggleActive(t)}
                        className={`relative inline-flex h-5 w-9 items-center rounded-full transition ${
                          t.active ? 'bg-emerald-500' : 'bg-secondary-300'
                        }`}>
                        <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition ${
                          t.active ? 'translate-x-4' : 'translate-x-1'
                        }`} />
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      <button
                        onClick={() => editingId === t.id ? cancelEdit() : startEdit(t)}
                        className={`px-2.5 py-1 text-xs rounded-lg font-medium transition ${
                          editingId === t.id
                            ? 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
                            : 'bg-primary-50 text-primary-700 hover:bg-primary-100'
                        }`}>
                        {editingId === t.id ? 'Cancel' : 'Edit'}
                      </button>
                    </td>
                  </tr>

                  {/* Inline edit form */}
                  {editingId === t.id && (
                    <tr>
                      <td colSpan={5} className="px-5 py-4 bg-secondary-50">
                        <div className="max-w-2xl space-y-4">
                          {error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm text-red-700">
                              {error}
                            </div>
                          )}

                          <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-1">Subject</label>
                            <input
                              type="text"
                              value={editForm.subject}
                              onChange={(e) => setEditForm({ ...editForm, subject: e.target.value })}
                              className="w-full px-4 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm"
                              placeholder="Notification subject..."
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-secondary-700 mb-1">Body</label>
                            <textarea
                              value={editForm.body}
                              onChange={(e) => setEditForm({ ...editForm, body: e.target.value })}
                              rows={6}
                              className="w-full px-4 py-2 border border-secondary-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none text-sm font-mono"
                              placeholder="Template body with {{variables}}..."
                            />
                          </div>

                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id={`active-${t.id}`}
                              checked={editForm.active}
                              onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })}
                              className="h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500"
                            />
                            <label htmlFor={`active-${t.id}`} className="text-sm text-secondary-700">Active</label>
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSave(t.id)}
                              disabled={saving}
                              className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition">
                              {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="px-4 py-2 text-sm font-medium rounded-lg border border-secondary-200 text-secondary-600 hover:bg-secondary-50 transition">
                              Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
